const MUTATING = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/** HTTP statuses safe to queue for later retry (local backend down / overloaded). */
export function shouldQueueHttpStatus(status: number): boolean {
  return status === 0 || status === 408 || status === 425 || status === 429 || status >= 500;
}

export function isNetworkError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const e = err as { name?: string; message?: string };
  if (e.name === 'TypeError' && /fetch|network|Failed to fetch|Load failed/i.test(e.message || '')) {
    return true;
  }
  if (/Failed to fetch|NetworkError|net::ERR/i.test(e.message || '')) return true;
  return false;
}

export function isMutatingMethod(method: string | undefined): boolean {
  return MUTATING.has((method || 'GET').toUpperCase());
}

/** Default TTL for last-good GET caches (stale still served offline). */
export const GET_CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000;
/** @deprecated Prefer GET_CACHE_TTL_MS — kept for SaaS callers. */
export const SAAS_GET_CACHE_TTL_MS = GET_CACHE_TTL_MS;

function splitCachePath(path: string): { pathname: string; search: string } {
  const raw = (path || '').trim();
  const qIdx = raw.indexOf('?');
  const pathname = ((qIdx >= 0 ? raw.slice(0, qIdx) : raw).replace(/\/+$/, '') || '/');
  const search = qIdx >= 0 ? raw.slice(qIdx + 1) : '';
  return { pathname, search };
}

/**
 * Normalize a request path for GET cache keys: strip trailing slash on pathname,
 * drop volatile `_` query params, sort remaining query keys.
 */
export function normalizeGetCachePath(path: string): string {
  const { pathname, search } = splitCachePath(path);
  if (!search) return pathname;

  const params = new URLSearchParams(search);
  params.delete('_');
  const entries: Array<[string, string]> = [];
  params.forEach((value, key) => {
    entries.push([key, value]);
  });
  entries.sort((a, b) => {
    const k = a[0].localeCompare(b[0]);
    return k !== 0 ? k : a[1].localeCompare(b[1]);
  });
  if (entries.length === 0) return pathname;
  const qs = new URLSearchParams(entries).toString();
  return `${pathname}?${qs}`;
}

/** @deprecated Prefer normalizeGetCachePath. */
export const normalizeSaasCachePath = normalizeGetCachePath;

function cacheQueryKeysAllowed(search: string, allowed: Set<string>): boolean {
  if (!search) return true;
  const params = new URLSearchParams(search);
  params.delete('_');
  let allowedOk = true;
  params.forEach((_value, key) => {
    if (!allowed.has(key)) allowedOk = false;
  });
  return allowedOk;
}

/**
 * SaaS last-good GET allowlist (depth A): safe list/read JSON only.
 * Never: live-preview, media bytes, AI, billing, admin, secrets-bearing nested GETs.
 */
export function isSaasCacheableGet(path: string): boolean {
  const { pathname, search } = splitCachePath(path);

  // Explicit denylist first
  if (
    /\/(live-preview|media|upload|ai|billing|checkout|stripe|admin|file)\b/i.test(pathname)
  ) {
    return false;
  }

  // Project list (optional limit / status filters only)
  if (pathname === '/api/saas/v1/projects') {
    return cacheQueryKeysAllowed(search, new Set(['limit', 'status']));
  }

  // Trash list — no volatile query
  if (pathname === '/api/saas/v1/projects/trash') {
    return cacheQueryKeysAllowed(search, new Set());
  }

  // Org / customer workspace lists (low-risk metadata)
  if (pathname === '/api/saas/v1/orgs') {
    return cacheQueryKeysAllowed(search, new Set());
  }
  if (pathname === '/api/saas/v1/customer-workspaces') {
    return cacheQueryKeysAllowed(search, new Set());
  }

  return false;
}

/**
 * Portal last-good GET: theme settings only.
 * Never: Comando Central, Freigabe, tenant domains, backups, health, media, AI, admin.
 */
export function isPortalCacheableGet(path: string): boolean {
  const { pathname, search } = splitCachePath(path);

  if (
    /\/(comando-central|freigabe|backup|tenant-domains?|provision|publish|media|upload|ai|admin|health|billing)\b/i.test(
      pathname,
    )
  ) {
    return false;
  }

  if (pathname === '/api/portal/v1/themes/settings') {
    return cacheQueryKeysAllowed(search, new Set());
  }

  return false;
}

/**
 * Platzel last-good GET: conversations list + paginated conversation thread.
 * Never: single-message / search / pin / scheduled GETs, media, push, calls, translate.
 */
export function isMessengerCacheableGet(path: string): boolean {
  const { pathname, search } = splitCachePath(path);

  // Thread history first — otherwise the broad /messages deny would block it.
  if (/^\/api\/messenger\/v1\/messages\/conversation\/[^/]+$/.test(pathname)) {
    return cacheQueryKeysAllowed(search, new Set(['limit', 'offset']));
  }

  if (
    /\/(messages|media|upload|calls?|webrtc|push|subscribe|read|pin|forward|reactions?|translate|typing|presence|attachment|summarize)\b/i.test(
      pathname,
    )
  ) {
    return false;
  }

  if (pathname === '/api/messenger/v1/conversations') {
    return cacheQueryKeysAllowed(search, new Set(['page', 'limit']));
  }

  return false;
}

/**
 * Dating last-good GET: own profile only.
 * Never: matches feed, likes, discovery, messages, media, AI.
 */
export function isDatingCacheableGet(path: string): boolean {
  const { pathname, search } = splitCachePath(path);

  if (
    /\/(likes?|matches?|messages?|media|upload|ai|subscriptions?|communities|events?|moderation|verifications?|admin|discovery|block|report)\b/i.test(
      pathname,
    )
  ) {
    return false;
  }

  if (pathname === '/api/dating/v1/profiles/me') {
    return cacheQueryKeysAllowed(search, new Set());
  }

  return false;
}

/**
 * PlatzView last-good GET: own channel metadata + own playlists list.
 * Never: public feed/contents, streams, uploads, AI, subscribe.
 */
export function isPlatzviewCacheableGet(path: string): boolean {
  const { pathname, search } = splitCachePath(path);

  if (
    /\/(upload|thumbnail|captions?|media|streams?|publish|block|like|dislike|view|progress|history|comments?|ai|admin|subscribe|unsubscribe|avatar|banner|storage|seen)\b/i.test(
      pathname,
    )
  ) {
    return false;
  }

  if (pathname === '/api/platzview/v1/channels/me') {
    return cacheQueryKeysAllowed(search, new Set());
  }

  if (pathname === '/api/platzview/v1/playlists') {
    const params = new URLSearchParams(search);
    params.delete('_');
    if (params.get('mine') !== 'true') return false;
    return cacheQueryKeysAllowed(search, new Set(['mine']));
  }

  return false;
}

/**
 * ToHear last-good GET: own show metadata + own playlists list.
 * Never: public episode feed, streams, uploads, AI, subscribe.
 */
export function isTohearCacheableGet(path: string): boolean {
  const { pathname, search } = splitCachePath(path);

  if (
    /\/(upload|artwork|banner|media|streams?|play|progress|history|pause|unpause|like|dislike|comments?|ai|admin|subscribe|unsubscribe|storage|seen)\b/i.test(
      pathname,
    )
  ) {
    return false;
  }

  if (pathname === '/api/tohear/v1/shows/me') {
    return cacheQueryKeysAllowed(search, new Set());
  }

  if (pathname === '/api/tohear/v1/playlists') {
    const params = new URLSearchParams(search);
    params.delete('_');
    if (params.get('mine') !== 'true') return false;
    return cacheQueryKeysAllowed(search, new Set(['mine']));
  }

  return false;
}

/**
 * Greetings last-good GET: cards list only.
 * Never: media, AI generate, send/share, single-card detail, admin.
 */
export function isGreetingsCacheableGet(path: string): boolean {
  const { pathname, search } = splitCachePath(path);

  if (
    /\/(media|upload|ai|generate|send|sharing|export|admin|publish|domain|auth)\b/i.test(pathname)
  ) {
    return false;
  }

  if (pathname === '/api/greetings/v1/cards') {
    return cacheQueryKeysAllowed(search, new Set(['page', 'limit', 'status']));
  }

  return false;
}

/**
 * Cut last-good GET: projects list + single project JSON document.
 * Never: media/blob, render status/history, backup zip, platzview-library, admin.
 */
export function isCutCacheableGet(path: string): boolean {
  const { pathname, search } = splitCachePath(path);

  if (
    /\/(media|upload|render|export|ffmpeg|import|import-zip|import-platzview|publish|publish-platzview|duplicate|backup|ai|admin|platzview-library|media-status)\b/i.test(
      pathname,
    )
  ) {
    return false;
  }

  if (pathname === '/api/cut/v1/projects') {
    return cacheQueryKeysAllowed(search, new Set());
  }

  if (/^\/api\/cut\/v1\/projects\/[^/]+$/.test(pathname)) {
    return cacheQueryKeysAllowed(search, new Set());
  }

  return false;
}

/**
 * Sound last-good GET: projects list + project session JSON.
 * Never: render/generate job status, audio asset bytes, daw-export downloads.
 */
export function isSoundCacheableGet(path: string): boolean {
  const { pathname, search } = splitCachePath(path);

  if (
    /\/(generate|infer|render|export|ffmpeg|bounce|duplicate|media|upload|assets|daw-export|admin|stem|diagnostics)\b/i.test(
      pathname,
    )
  ) {
    return false;
  }

  if (pathname === '/api/sound/v1/projects') {
    return cacheQueryKeysAllowed(search, new Set(['archived']));
  }

  if (/^\/api\/sound\/v1\/projects\/[^/]+$/.test(pathname)) {
    return cacheQueryKeysAllowed(search, new Set());
  }

  return false;
}

/**
 * DJ last-good GET: projects / crates / schedules lists.
 * Never: stream, transport, waveform, assets, recordings, now-playing, live.
 */
export function isDjCacheableGet(path: string): boolean {
  const { pathname, search } = splitCachePath(path);

  if (
    /\/(upload|assets|media|stream|waveform|reanalyze|transport|next|queue|transition-suggest|recordings?|publish|simulate|ai|admin|now-playing|analysis|collaborators|sessions|blocks)\b/i.test(
      pathname,
    )
  ) {
    return false;
  }

  if (pathname === '/api/dj/v1/projects') {
    return cacheQueryKeysAllowed(search, new Set());
  }

  if (/^\/api\/dj\/v1\/projects\/[^/]+\/crates$/.test(pathname)) {
    return cacheQueryKeysAllowed(search, new Set());
  }

  if (/^\/api\/dj\/v1\/projects\/[^/]+\/schedules$/.test(pathname)) {
    return cacheQueryKeysAllowed(search, new Set());
  }

  if (pathname === '/api/dj/v1/schedules') {
    return cacheQueryKeysAllowed(search, new Set(['projectId']));
  }

  return false;
}

/**
 * Tradalous last-good GET: journal list (+ optional entry-by-id text).
 * Never: /ai/**, progress XP (must stay live), import, admin.
 */
export function isTradalousCacheableGet(path: string): boolean {
  const { pathname, search } = splitCachePath(path);

  if (
    /\/(ai|translate|infer|generate|analyze|explain|journal-feedback|import|upload|media|admin|progress)\b/i.test(
      pathname,
    )
  ) {
    return false;
  }

  if (pathname === '/api/tradalous/v1/journal') {
    return cacheQueryKeysAllowed(search, new Set(['category', 'timeframe', 'q', 'limit', 'offset']));
  }

  if (/^\/api\/tradalous\/v1\/journal\/[^/]+$/.test(pathname)) {
    return cacheQueryKeysAllowed(search, new Set());
  }

  return false;
}

/**
 * AI Music last-good GET: projects list, project metadata, settings.
 * Never: generate/render/export job status, assets, training/voice, admin.
 */
export function isAiMusicCacheableGet(path: string): boolean {
  const { pathname, search } = splitCachePath(path);

  if (
    /\/(generate|infer|render|export|media|upload|assets|assistant|voice-profiles|lyrics|admin|train|samples|consent|attach|diagnostics|tracks)\b/i.test(
      pathname,
    )
  ) {
    return false;
  }

  if (pathname === '/api/ai-music/v1/projects') {
    return cacheQueryKeysAllowed(search, new Set());
  }

  if (/^\/api\/ai-music\/v1\/projects\/[^/]+$/.test(pathname)) {
    return cacheQueryKeysAllowed(search, new Set());
  }

  if (pathname === '/api/ai-music/v1/settings') {
    return cacheQueryKeysAllowed(search, new Set());
  }

  return false;
}

/**
 * Central media library last-good GET: list metadata JSON only (`GET /api/media/v1`).
 * Hard deny: `/file` blob bytes, upload, dedup, orphans, favorite, single-asset GETs.
 * Opt-in at call site via `isMediaListCacheEnabled()` — this allowlist alone is not enough.
 */
export function isMediaCacheableGet(path: string): boolean {
  const { pathname, search } = splitCachePath(path);

  if (/\/(file|upload|dedup|orphans|favorite|sweep)\b/i.test(pathname)) {
    return false;
  }

  const normalized = pathname.replace(/\/+$/, '') || '/';
  if (normalized === '/api/media/v1') {
    return cacheQueryKeysAllowed(
      search,
      new Set(['platform', 'type', 'tags', 'favorite', 'q', 'folderPath', 'page', 'limit']),
    );
  }

  return false;
}

/**
 * AI Image/Video last-good GET: projects list, project metadata, settings.
 * Never: generate/render job status, asset URLs, training, images/videos bytes.
 */
export function isAiImageVideoCacheableGet(path: string): boolean {
  const { pathname, search } = splitCachePath(path);

  if (
    /\/(generate|infer|render|export|media|upload|assets|training|thumbnail|images|videos|galleries|admin|diagnostics)\b/i.test(
      pathname,
    )
  ) {
    return false;
  }

  if (pathname === '/api/ai-image-video/v1/projects') {
    return cacheQueryKeysAllowed(search, new Set(['page', 'limit', 'type']));
  }

  if (/^\/api\/ai-image-video\/v1\/projects\/[^/]+$/.test(pathname)) {
    return cacheQueryKeysAllowed(search, new Set());
  }

  if (pathname === '/api/ai-image-video/v1/settings') {
    return cacheQueryKeysAllowed(search, new Set());
  }

  return false;
}

/** IndexedDB KV key for a cacheable GET. */
export function getCacheKey(path: string): string {
  return `GET:${normalizeGetCachePath(path)}`;
}

/** @deprecated Prefer getCacheKey. */
export const saasGetCacheKey = getCacheKey;

/**
 * SaaS allowlist (Wave 1 depth A): project/page/content JSON CRUD.
 * Never: media upload, AI, publish/Freigabe, payments, binary.
 */
export function isSaasQueueableMutation(method: string, path: string): boolean {
  if (!isMutatingMethod(method)) return false;
  const p = path.split('?')[0];

  // Explicit denylist first
  if (
    /\/publish\b|\/unpublish\b|\/media\b|\/upload\b|\/ai\b|\/billing\b|\/checkout\b|\/stripe\b|\/live-preview\b/i.test(
      p,
    )
  ) {
    return false;
  }

  // Project + nested content
  if (/^\/api\/saas\/v1\/projects(\/|$)/.test(p)) {
    // Allow project create/update/delete/restore/pages/blog/collections (JSON)
    if (/\/hard-delete\b/.test(p)) return true;
    if (/\/restore\b/.test(p)) return true;
    if (/\/pages\b/.test(p)) return true;
    if (/\/blog-posts\b/.test(p)) return true;
    if (/\/collections\b/.test(p)) return true;
    if (/\/versions\/[^/]+\/restore\b/.test(p)) return true;
    if (/^\/api\/saas\/v1\/projects\/?$/.test(p)) return true; // POST create
    if (/^\/api\/saas\/v1\/projects\/from-template\//.test(p)) return true;
    if (/^\/api\/saas\/v1\/projects\/[^/]+\/?$/.test(p)) return true; // PUT/DELETE project
    return false;
  }

  // Orgs / customer workspaces / settings (JSON)
  if (/^\/api\/saas\/v1\/(orgs|organizations|customers|customer-workspaces|customer-workspace-invites|workspaces|settings)(\/|$)/.test(p)) {
    return true;
  }

  return false;
}

export function entityKeyFromPath(path: string): string | undefined {
  const m = path.match(/\/projects\/([^/?]+)/);
  return m?.[1] ? `project:${m[1]}` : undefined;
}

/**
 * Platzel allowlist (v1+): text message create + mark-read / read-all.
 * Never: media/upload, calls/WebRTC, push subscribe, reactions, pin/forward, presence/typing.
 * Text create requires JSON with type "text" (or omitted → text) and no attachment.
 * Mark-read endpoints accept empty bodies (idempotent POSTs).
 */
export function isMessengerQueueableMutation(
  method: string,
  path: string,
  body?: string | null,
): boolean {
  if ((method || '').toUpperCase() !== 'POST') return false;
  const p = (path.split('?')[0] || '').replace(/\/$/, '');

  // Mark-read first (idempotent). Must precede the `/read` denylist below.
  if (/^\/api\/messenger\/v1\/messages\/[^/]+\/read$/.test(p)) return true;
  if (/^\/api\/messenger\/v1\/messages\/conversation\/[^/]+\/read-all$/.test(p)) return true;

  // Defense-in-depth denylist (also blocks nested /messages/... actions)
  if (
    /\/(upload|media|calls?|webrtc|push|subscribe|read|pin|forward|reactions?|translate|typing|presence|attachment)\b/i.test(
      p,
    )
  ) {
    return false;
  }

  if (p !== '/api/messenger/v1/messages') return false;
  if (body == null || body === '') return false;

  try {
    const parsed = JSON.parse(body) as Record<string, unknown>;
    const type = String(parsed.type ?? 'text').toLowerCase();
    if (type !== 'text') return false;
    if (parsed.attachment != null) return false;
    if (typeof parsed.conversationId !== 'string' || !parsed.conversationId) return false;
    if (typeof parsed.content !== 'string') return false;
    // Prefer stable clientMessageId for outbox ↔ optimistic UI reconcile
    if (parsed.clientMessageId != null && typeof parsed.clientMessageId !== 'string') return false;
    return true;
  } catch {
    return false;
  }
}

/** Outbox coalesce key for Messenger mark-read paths (distinct from text `conversation:` keys). */
export function messengerEntityKeyFromPath(path: string): string | undefined {
  const p = (path.split('?')[0] || '').replace(/\/$/, '');
  const readAll = p.match(/^\/api\/messenger\/v1\/messages\/conversation\/([^/]+)\/read-all$/);
  if (readAll?.[1]) return `conversation-read:${readAll[1]}`;
  const readOne = p.match(/^\/api\/messenger\/v1\/messages\/([^/]+)\/read$/);
  if (readOne?.[1]) return `message-read:${readOne[1]}`;
  return undefined;
}

export function messengerEntityKeyFromBody(body: string | null | undefined): string | undefined {
  if (!body) return undefined;
  try {
    const parsed = JSON.parse(body) as { conversationId?: string };
    return parsed.conversationId ? `conversation:${parsed.conversationId}` : undefined;
  } catch {
    return undefined;
  }
}

/** Extract Platzel `clientMessageId` from a queued JSON body (for UI reconcile). */
export function clientMessageIdFromBody(body: string | null | undefined): string | undefined {
  if (!body) return undefined;
  try {
    const parsed = JSON.parse(body) as { clientMessageId?: unknown };
    return typeof parsed.clientMessageId === 'string' && parsed.clientMessageId
      ? parsed.clientMessageId
      : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Portal allowlist: prefs / profile JSON + theme settings CRUD only.
 * Never: Comando Central, Freigabe online/offline, backups, tenant domain provision,
 * auth login/logout/password, admin, media upload, AI.
 */
export function isPortalQueueableMutation(method: string, path: string): boolean {
  if (!isMutatingMethod(method)) return false;
  const p = (path.split('?')[0] || '').replace(/\/$/, '');
  const m = (method || '').toUpperCase();

  // Explicit denylist (defense-in-depth; CC / Freigabe / auth / AI / media / backups)
  if (
    /\/comando-central\b|\/freigabe\b|\/backup\b|\/tenant-domains?\b|\/provision\b|\/publish\b|\/unpublish\b|\/online\b|\/offline\b|\/media\b|\/upload\b|\/ai\b|\/admin\b|\/billing\b|\/checkout\b/i.test(
      p,
    )
  ) {
    return false;
  }
  if (/^\/api\/auth\/(login|logout|register|refresh|verify|resend|forgot|reset|change-password|find-email)/i.test(p)) {
    return false;
  }

  // Portal prefs (favorites / tags)
  if (m === 'PATCH' && p === '/api/auth/profile/prefs') return true;

  // Profile display fields (username / displayName / bio) — not password / admin
  if (m === 'PATCH' && p === '/api/auth/profile') return true;

  // Theme settings + user theme JSON CRUD (no binary)
  if (m === 'PUT' && p === '/api/portal/v1/themes/settings') return true;
  if (m === 'POST' && p === '/api/portal/v1/themes') return true;
  if ((m === 'PUT' || m === 'DELETE') && /^\/api\/portal\/v1\/themes\/[^/]+$/.test(p)) return true;

  return false;
}

export function portalEntityKeyFromPath(path: string): string | undefined {
  const p = (path.split('?')[0] || '').replace(/\/$/, '');
  if (p === '/api/auth/profile/prefs') return 'portal:prefs';
  if (p === '/api/auth/profile') return 'portal:profile';
  if (p === '/api/portal/v1/themes/settings') return 'portal:theme-settings';
  if (p === '/api/portal/v1/themes') return 'portal:themes';
  const theme = p.match(/^\/api\/portal\/v1\/themes\/([^/]+)$/);
  return theme?.[1] ? `portal:theme:${theme[1]}` : undefined;
}

/**
 * Greetings allowlist: greeting card JSON create / update / delete only.
 * Never: media upload, AI generate, send/share/export, domain settings, admin, auth.
 */
export function isGreetingsQueueableMutation(method: string, path: string): boolean {
  if (!isMutatingMethod(method)) return false;
  const p = (path.split('?')[0] || '').replace(/\/$/, '');
  const m = (method || '').toUpperCase();

  // Defense-in-depth denylist
  if (
    /\/(media|upload|ai|generate|send|sharing|export|admin|publish|domain|auth)\b/i.test(p)
  ) {
    return false;
  }
  if (/^\/api\/auth\//i.test(p)) return false;

  // Card collection create
  if (m === 'POST' && p === '/api/greetings/v1/cards') return true;

  // Card document update / delete (no nested actions like /send)
  if ((m === 'PUT' || m === 'DELETE') && /^\/api\/greetings\/v1\/cards\/[^/]+$/.test(p)) {
    return true;
  }

  return false;
}

export function greetingsEntityKeyFromPath(path: string): string | undefined {
  const p = (path.split('?')[0] || '').replace(/\/$/, '');
  if (p === '/api/greetings/v1/cards') return 'greetings:cards';
  const card = p.match(/^\/api\/greetings\/v1\/cards\/([^/]+)$/);
  return card?.[1] ? `greetings:card:${card[1]}` : undefined;
}

/**
 * Cut allowlist: project document JSON create / update only.
 * Never: media/blob upload, import-zip, render/export/FFmpeg, publish-platzview,
 * duplicate, backup download, AI, large asset PUT.
 */
export function isCutQueueableMutation(method: string, path: string): boolean {
  if (!isMutatingMethod(method)) return false;
  const p = (path.split('?')[0] || '').replace(/\/$/, '');
  const m = (method || '').toUpperCase();

  // Defense-in-depth denylist (media / render / import / publish / AI / backup)
  if (
    /\/(media|upload|render|export|ffmpeg|import|import-zip|import-platzview|publish|publish-platzview|duplicate|backup|ai|admin)\b/i.test(
      p,
    )
  ) {
    return false;
  }
  if (/^\/api\/auth\//i.test(p)) return false;

  // Create project (JSON title / aspectRatio only — no FormData)
  if (m === 'POST' && p === '/api/cut/v1/projects') return true;

  // Project document autosave / update (JSON scenes timeline — no nested media)
  if (m === 'PUT' && /^\/api\/cut\/v1\/projects\/[^/]+$/.test(p)) return true;

  return false;
}

export function cutEntityKeyFromPath(path: string): string | undefined {
  const p = (path.split('?')[0] || '').replace(/\/$/, '');
  if (p === '/api/cut/v1/projects') return 'cut:projects';
  const project = p.match(/^\/api\/cut\/v1\/projects\/([^/]+)$/);
  return project?.[1] ? `cut:project:${project[1]}` : undefined;
}

/**
 * Dating allowlist: profile JSON create/update + theme settings CRUD only.
 * Never: likes/swipes, matches, messages, media upload, AI, subscriptions,
 * communities, events, moderation, verification, admin, DELETE profile.
 */
export function isDatingQueueableMutation(method: string, path: string): boolean {
  if (!isMutatingMethod(method)) return false;
  const p = (path.split('?')[0] || '').replace(/\/$/, '');
  const m = (method || '').toUpperCase();

  if (
    /\/(likes?|matches?|messages?|media|upload|ai|subscriptions?|communities|events?|moderation|verifications?|admin|discovery|block|report)\b/i.test(
      p,
    )
  ) {
    return false;
  }
  if (/^\/api\/auth\//i.test(p)) return false;

  // Profile create + update (JSON prefs / bio / interests — not media)
  if (m === 'POST' && p === '/api/dating/v1/profiles') return true;
  if (m === 'PUT' && p === '/api/dating/v1/profiles/me') return true;

  // Theme settings + user theme JSON CRUD
  if (m === 'PUT' && p === '/api/dating/v1/themes/settings') return true;
  if (m === 'POST' && p === '/api/dating/v1/themes') return true;
  if ((m === 'PUT' || m === 'DELETE') && /^\/api\/dating\/v1\/themes\/[^/]+$/.test(p)) return true;

  return false;
}

export function datingEntityKeyFromPath(path: string): string | undefined {
  const p = (path.split('?')[0] || '').replace(/\/$/, '');
  if (p === '/api/dating/v1/profiles' || p === '/api/dating/v1/profiles/me') return 'dating:profile';
  if (p === '/api/dating/v1/themes/settings') return 'dating:theme-settings';
  if (p === '/api/dating/v1/themes') return 'dating:themes';
  const theme = p.match(/^\/api\/dating\/v1\/themes\/([^/]+)$/);
  return theme?.[1] ? `dating:theme:${theme[1]}` : undefined;
}

/**
 * PlatzView allowlist: content metadata create/update, playlist JSON CRUD, channel
 * metadata update, theme settings. Never: media/thumbnail/caption upload,
 * streaming, publish/block, likes, comments, AI, admin, subscribe, progress.
 */
export function isPlatzviewQueueableMutation(method: string, path: string): boolean {
  if (!isMutatingMethod(method)) return false;
  const p = (path.split('?')[0] || '').replace(/\/$/, '');
  const m = (method || '').toUpperCase();

  if (
    /\/(upload|thumbnail|captions?|media|streams?|publish|block|pause|unpause|like|dislike|view|progress|history|comments?|ai|admin|subscribe|unsubscribe|avatar|banner|storage|seen)\b/i.test(
      p,
    )
  ) {
    return false;
  }
  if (/^\/api\/auth\//i.test(p)) return false;

  // Content draft create + metadata update (no nested actions, no DELETE)
  if (m === 'POST' && p === '/api/platzview/v1/contents') return true;
  if (m === 'PUT' && /^\/api\/platzview\/v1\/contents\/[^/]+$/.test(p)) return true;

  // Playlist JSON CRUD
  if (m === 'POST' && p === '/api/platzview/v1/playlists') return true;
  if ((m === 'PUT' || m === 'DELETE') && /^\/api\/platzview\/v1\/playlists\/[^/]+$/.test(p)) return true;
  if (m === 'POST' && /^\/api\/platzview\/v1\/playlists\/[^/]+\/add$/.test(p)) return true;
  if (m === 'PUT' && /^\/api\/platzview\/v1\/playlists\/[^/]+\/reorder$/.test(p)) return true;
  if (m === 'DELETE' && /^\/api\/platzview\/v1\/playlists\/[^/]+\/remove\/[^/]+$/.test(p)) return true;

  // Channel metadata update only
  if (m === 'PUT' && /^\/api\/platzview\/v1\/channels\/[^/]+$/.test(p)) return true;

  // Theme settings + user theme JSON CRUD
  if (m === 'PUT' && p === '/api/platzview/v1/themes/settings') return true;
  if (m === 'POST' && p === '/api/platzview/v1/themes') return true;
  if ((m === 'PUT' || m === 'DELETE') && /^\/api\/platzview\/v1\/themes\/[^/]+$/.test(p)) return true;

  return false;
}

export function platzviewEntityKeyFromPath(path: string): string | undefined {
  const p = (path.split('?')[0] || '').replace(/\/$/, '');
  if (p === '/api/platzview/v1/contents') return 'platzview:contents';
  const content = p.match(/^\/api\/platzview\/v1\/contents\/([^/]+)$/);
  if (content?.[1]) return `platzview:content:${content[1]}`;
  if (p === '/api/platzview/v1/playlists') return 'platzview:playlists';
  const playlist = p.match(/^\/api\/platzview\/v1\/playlists\/([^/]+)/);
  if (playlist?.[1]) return `platzview:playlist:${playlist[1]}`;
  const channel = p.match(/^\/api\/platzview\/v1\/channels\/([^/]+)$/);
  if (channel?.[1]) return `platzview:channel:${channel[1]}`;
  if (p === '/api/platzview/v1/themes/settings') return 'platzview:theme-settings';
  if (p === '/api/platzview/v1/themes') return 'platzview:themes';
  const theme = p.match(/^\/api\/platzview\/v1\/themes\/([^/]+)$/);
  return theme?.[1] ? `platzview:theme:${theme[1]}` : undefined;
}

/**
 * ToHear allowlist: episode metadata create/update, playlist JSON CRUD, show
 * metadata update, theme settings. Never: audio/artwork upload, streaming,
 * play/progress, likes, comments, AI, admin, subscribe, pause/unpause.
 */
export function isTohearQueueableMutation(method: string, path: string): boolean {
  if (!isMutatingMethod(method)) return false;
  const p = (path.split('?')[0] || '').replace(/\/$/, '');
  const m = (method || '').toUpperCase();

  if (
    /\/(upload|artwork|banner|media|streams?|play|progress|history|pause|unpause|like|dislike|comments?|ai|admin|subscribe|unsubscribe|storage|seen)\b/i.test(
      p,
    )
  ) {
    return false;
  }
  if (/^\/api\/auth\//i.test(p)) return false;

  // Episode draft create + metadata update (no nested actions, no DELETE)
  if (m === 'POST' && p === '/api/tohear/v1/episodes') return true;
  if (m === 'PUT' && /^\/api\/tohear\/v1\/episodes\/[^/]+$/.test(p)) return true;

  // Playlist JSON CRUD
  if (m === 'POST' && p === '/api/tohear/v1/playlists') return true;
  if ((m === 'PUT' || m === 'DELETE') && /^\/api\/tohear\/v1\/playlists\/[^/]+$/.test(p)) return true;
  if (m === 'POST' && /^\/api\/tohear\/v1\/playlists\/[^/]+\/add$/.test(p)) return true;
  if (m === 'PUT' && /^\/api\/tohear\/v1\/playlists\/[^/]+\/reorder$/.test(p)) return true;
  if (m === 'DELETE' && /^\/api\/tohear\/v1\/playlists\/[^/]+\/remove\/[^/]+$/.test(p)) return true;

  // Show metadata update only
  if (m === 'PUT' && /^\/api\/tohear\/v1\/shows\/[^/]+$/.test(p)) return true;

  // Theme settings + user theme JSON CRUD
  if (m === 'PUT' && p === '/api/tohear/v1/themes/settings') return true;
  if (m === 'POST' && p === '/api/tohear/v1/themes') return true;
  if ((m === 'PUT' || m === 'DELETE') && /^\/api\/tohear\/v1\/themes\/[^/]+$/.test(p)) return true;

  return false;
}

export function tohearEntityKeyFromPath(path: string): string | undefined {
  const p = (path.split('?')[0] || '').replace(/\/$/, '');
  if (p === '/api/tohear/v1/episodes') return 'tohear:episodes';
  const episode = p.match(/^\/api\/tohear\/v1\/episodes\/([^/]+)$/);
  if (episode?.[1]) return `tohear:episode:${episode[1]}`;
  if (p === '/api/tohear/v1/playlists') return 'tohear:playlists';
  const playlist = p.match(/^\/api\/tohear\/v1\/playlists\/([^/]+)/);
  if (playlist?.[1]) return `tohear:playlist:${playlist[1]}`;
  const show = p.match(/^\/api\/tohear\/v1\/shows\/([^/]+)$/);
  if (show?.[1]) return `tohear:show:${show[1]}`;
  if (p === '/api/tohear/v1/themes/settings') return 'tohear:theme-settings';
  if (p === '/api/tohear/v1/themes') return 'tohear:themes';
  const theme = p.match(/^\/api\/tohear\/v1\/themes\/([^/]+)$/);
  return theme?.[1] ? `tohear:theme:${theme[1]}` : undefined;
}

/**
 * AI Music allowlist: project metadata create/update + theme/settings JSON only.
 * Never: generate/infer, tracks generate, render/export, assets/upload, assistant,
 * voice train/samples, lyrics generate, admin.
 */
export function isAiMusicQueueableMutation(method: string, path: string): boolean {
  if (!isMutatingMethod(method)) return false;
  const p = (path.split('?')[0] || '').replace(/\/$/, '');
  const m = (method || '').toUpperCase();

  if (
    /\/(generate|infer|render|export|media|upload|assets|assistant|voice-profiles|lyrics|admin|train|samples|consent|attach)\b/i.test(
      p,
    )
  ) {
    return false;
  }
  if (/^\/api\/auth\//i.test(p)) return false;

  // Project metadata create / rename / update (no nested archive/delete)
  if (m === 'POST' && p === '/api/ai-music/v1/projects') return true;
  if (m === 'PUT' && /^\/api\/ai-music\/v1\/projects\/[^/]+$/.test(p)) return true;

  // Studio prefs JSON (not language path — that is settings/language)
  if (m === 'PUT' && p === '/api/ai-music/v1/settings') return true;

  // Theme settings + user theme JSON CRUD
  if (m === 'PUT' && p === '/api/ai-music/v1/themes/settings') return true;
  if (m === 'POST' && p === '/api/ai-music/v1/themes') return true;
  if ((m === 'PUT' || m === 'DELETE') && /^\/api\/ai-music\/v1\/themes\/[^/]+$/.test(p)) return true;

  return false;
}

export function aiMusicEntityKeyFromPath(path: string): string | undefined {
  const p = (path.split('?')[0] || '').replace(/\/$/, '');
  if (p === '/api/ai-music/v1/projects') return 'ai-music:projects';
  const project = p.match(/^\/api\/ai-music\/v1\/projects\/([^/]+)$/);
  if (project?.[1]) return `ai-music:project:${project[1]}`;
  if (p === '/api/ai-music/v1/settings') return 'ai-music:settings';
  if (p === '/api/ai-music/v1/themes/settings') return 'ai-music:theme-settings';
  if (p === '/api/ai-music/v1/themes') return 'ai-music:themes';
  const theme = p.match(/^\/api\/ai-music\/v1\/themes\/([^/]+)$/);
  return theme?.[1] ? `ai-music:theme:${theme[1]}` : undefined;
}

/**
 * AI Image/Video allowlist: project metadata create/update + theme/settings JSON only.
 * Never: image/video generate, render, training, assets/upload, thumbnails, admin.
 */
export function isAiImageVideoQueueableMutation(method: string, path: string): boolean {
  if (!isMutatingMethod(method)) return false;
  const p = (path.split('?')[0] || '').replace(/\/$/, '');
  const m = (method || '').toUpperCase();

  if (
    /\/(generate|infer|render|export|media|upload|assets|training|thumbnail|images|videos|galleries|admin)\b/i.test(
      p,
    )
  ) {
    return false;
  }
  if (/^\/api\/auth\//i.test(p)) return false;

  if (m === 'POST' && p === '/api/ai-image-video/v1/projects') return true;
  if (m === 'PUT' && /^\/api\/ai-image-video\/v1\/projects\/[^/]+$/.test(p)) return true;

  if (m === 'PUT' && p === '/api/ai-image-video/v1/settings') return true;

  if (m === 'PUT' && p === '/api/ai-image-video/v1/themes/settings') return true;
  if (m === 'POST' && p === '/api/ai-image-video/v1/themes') return true;
  if ((m === 'PUT' || m === 'DELETE') && /^\/api\/ai-image-video\/v1\/themes\/[^/]+$/.test(p)) {
    return true;
  }

  return false;
}

export function aiImageVideoEntityKeyFromPath(path: string): string | undefined {
  const p = (path.split('?')[0] || '').replace(/\/$/, '');
  if (p === '/api/ai-image-video/v1/projects') return 'ai-image-video:projects';
  const project = p.match(/^\/api\/ai-image-video\/v1\/projects\/([^/]+)$/);
  if (project?.[1]) return `ai-image-video:project:${project[1]}`;
  if (p === '/api/ai-image-video/v1/settings') return 'ai-image-video:settings';
  if (p === '/api/ai-image-video/v1/themes/settings') return 'ai-image-video:theme-settings';
  if (p === '/api/ai-image-video/v1/themes') return 'ai-image-video:themes';
  const theme = p.match(/^\/api\/ai-image-video\/v1\/themes\/([^/]+)$/);
  return theme?.[1] ? `ai-image-video:theme:${theme[1]}` : undefined;
}

/**
 * Sound allowlist: project + track session JSON + theme settings.
 * Never: generate, render/export/FFmpeg, bounce, duplicate, assets/upload,
 * daw-export, AI workers. Studio prefs/markers stay in localStorage (untouched).
 */
export function isSoundQueueableMutation(method: string, path: string): boolean {
  if (!isMutatingMethod(method)) return false;
  const p = (path.split('?')[0] || '').replace(/\/$/, '');
  const m = (method || '').toUpperCase();

  if (
    /\/(generate|infer|render|export|ffmpeg|bounce|duplicate|media|upload|assets|daw-export|admin|stem)\b/i.test(
      p,
    )
  ) {
    return false;
  }
  if (/^\/api\/auth\//i.test(p)) return false;

  // Project create / session document update
  if (m === 'POST' && p === '/api/sound/v1/projects') return true;
  if (m === 'PUT' && /^\/api\/sound\/v1\/projects\/[^/]+$/.test(p)) return true;

  // Track session JSON (create / update / reorder — not bounce/duplicate)
  if (m === 'POST' && /^\/api\/sound\/v1\/projects\/[^/]+\/tracks$/.test(p)) return true;
  if (m === 'PUT' && /^\/api\/sound\/v1\/projects\/[^/]+\/tracks\/[^/]+$/.test(p)) return true;
  if (m === 'POST' && /^\/api\/sound\/v1\/projects\/[^/]+\/tracks\/reorder$/.test(p)) return true;

  // Theme settings + user theme JSON CRUD
  if (m === 'PUT' && p === '/api/sound/v1/themes/settings') return true;
  if (m === 'POST' && p === '/api/sound/v1/themes') return true;
  if ((m === 'PUT' || m === 'DELETE') && /^\/api\/sound\/v1\/themes\/[^/]+$/.test(p)) return true;

  return false;
}

export function soundEntityKeyFromPath(path: string): string | undefined {
  const p = (path.split('?')[0] || '').replace(/\/$/, '');
  if (p === '/api/sound/v1/projects') return 'sound:projects';
  const track = p.match(/^\/api\/sound\/v1\/projects\/([^/]+)\/tracks/);
  if (track?.[1]) return `sound:project:${track[1]}:tracks`;
  const project = p.match(/^\/api\/sound\/v1\/projects\/([^/]+)$/);
  if (project?.[1]) return `sound:project:${project[1]}`;
  if (p === '/api/sound/v1/themes/settings') return 'sound:theme-settings';
  if (p === '/api/sound/v1/themes') return 'sound:themes';
  const theme = p.match(/^\/api\/sound\/v1\/themes\/([^/]+)$/);
  return theme?.[1] ? `sound:theme:${theme[1]}` : undefined;
}

/**
 * DJ allowlist: project / crate (setlist) / session state / block / schedule JSON.
 * Never: live transport/stream, audio upload, recordings, publish, simulate,
 * reanalyze, AI.
 */
export function isDjQueueableMutation(method: string, path: string): boolean {
  if (!isMutatingMethod(method)) return false;
  const p = (path.split('?')[0] || '').replace(/\/$/, '');
  const m = (method || '').toUpperCase();

  if (
    /\/(upload|assets|media|stream|waveform|reanalyze|transport|next|queue|transition-suggest|recordings?|publish|simulate|ai|admin)\b/i.test(
      p,
    )
  ) {
    return false;
  }
  if (/^\/api\/auth\//i.test(p)) return false;

  // Project metadata
  if (m === 'POST' && p === '/api/dj/v1/projects') return true;
  if (m === 'PUT' && /^\/api\/dj\/v1\/projects\/[^/]+$/.test(p)) return true;

  // Crates (setlists)
  if (m === 'POST' && /^\/api\/dj\/v1\/projects\/[^/]+\/crates$/.test(p)) return true;
  if (
    (m === 'PUT' || m === 'DELETE') &&
    /^\/api\/dj\/v1\/projects\/[^/]+\/crates\/[^/]+$/.test(p)
  ) {
    return true;
  }

  // Session create + state save (setlist metadata)
  if (m === 'POST' && /^\/api\/dj\/v1\/projects\/[^/]+\/sessions$/.test(p)) return true;
  if (m === 'PATCH' && /^\/api\/dj\/v1\/sessions\/[^/]+\/state$/.test(p)) return true;

  // Blocks JSON under session
  if (m === 'POST' && /^\/api\/dj\/v1\/sessions\/[^/]+\/blocks$/.test(p)) return true;
  if (
    (m === 'PUT' || m === 'DELETE') &&
    /^\/api\/dj\/v1\/sessions\/[^/]+\/blocks\/[^/]+$/.test(p)
  ) {
    return true;
  }

  // Schedules JSON (not simulate)
  if (m === 'POST' && p === '/api/dj/v1/schedules') return true;
  if ((m === 'PUT' || m === 'DELETE') && /^\/api\/dj\/v1\/schedules\/[^/]+$/.test(p)) return true;

  return false;
}

export function djEntityKeyFromPath(path: string): string | undefined {
  const p = (path.split('?')[0] || '').replace(/\/$/, '');
  if (p === '/api/dj/v1/projects') return 'dj:projects';
  const crate = p.match(/^\/api\/dj\/v1\/projects\/([^/]+)\/crates/);
  if (crate?.[1]) return `dj:project:${crate[1]}:crates`;
  const sessions = p.match(/^\/api\/dj\/v1\/projects\/([^/]+)\/sessions$/);
  if (sessions?.[1]) return `dj:project:${sessions[1]}:sessions`;
  const project = p.match(/^\/api\/dj\/v1\/projects\/([^/]+)$/);
  if (project?.[1]) return `dj:project:${project[1]}`;
  const sessionState = p.match(/^\/api\/dj\/v1\/sessions\/([^/]+)\/state$/);
  if (sessionState?.[1]) return `dj:session:${sessionState[1]}`;
  const block = p.match(/^\/api\/dj\/v1\/sessions\/([^/]+)\/blocks/);
  if (block?.[1]) return `dj:session:${block[1]}:blocks`;
  if (p === '/api/dj/v1/schedules') return 'dj:schedules';
  const schedule = p.match(/^\/api\/dj\/v1\/schedules\/([^/]+)$/);
  return schedule?.[1] ? `dj:schedule:${schedule[1]}` : undefined;
}

/**
 * Tradalous allowlist: journal draft text JSON + theme settings.
 * Never: /ai/** (analyze/explain/generate/journal-feedback), journal import,
 * admin CMS, file upload. Progress XP stays online-only.
 */
export function isTradalousQueueableMutation(method: string, path: string): boolean {
  if (!isMutatingMethod(method)) return false;
  const p = (path.split('?')[0] || '').replace(/\/$/, '');
  const m = (method || '').toUpperCase();

  if (
    /\/(ai|translate|infer|generate|analyze|explain|journal-feedback|import|upload|media|admin)\b/i.test(
      p,
    )
  ) {
    return false;
  }
  if (/^\/api\/auth\//i.test(p)) return false;

  // Journal draft text CRUD
  if (m === 'POST' && p === '/api/tradalous/v1/journal') return true;
  if ((m === 'PUT' || m === 'DELETE') && /^\/api\/tradalous\/v1\/journal\/[^/]+$/.test(p)) {
    return true;
  }

  // Theme settings + user theme JSON CRUD
  if (m === 'PUT' && p === '/api/tradalous/v1/themes/settings') return true;
  if (m === 'POST' && p === '/api/tradalous/v1/themes') return true;
  if ((m === 'PUT' || m === 'DELETE') && /^\/api\/tradalous\/v1\/themes\/[^/]+$/.test(p)) {
    return true;
  }

  return false;
}

export function tradalousEntityKeyFromPath(path: string): string | undefined {
  const p = (path.split('?')[0] || '').replace(/\/$/, '');
  if (p === '/api/tradalous/v1/journal') return 'tradalous:journal';
  const entry = p.match(/^\/api\/tradalous\/v1\/journal\/([^/]+)$/);
  if (entry?.[1]) return `tradalous:journal:${entry[1]}`;
  if (p === '/api/tradalous/v1/themes/settings') return 'tradalous:theme-settings';
  if (p === '/api/tradalous/v1/themes') return 'tradalous:themes';
  const theme = p.match(/^\/api\/tradalous\/v1\/themes\/([^/]+)$/);
  return theme?.[1] ? `tradalous:theme:${theme[1]}` : undefined;
}

/**
 * Rechtshilfe last-good GET: case list/summary, own profile, theme settings.
 * Never: nested case workspace, search, messages, documents, evidence, export, admin, AI.
 */
export function isRechtshilfeCacheableGet(path: string): boolean {
  const { pathname, search } = splitCachePath(path);

  if (
    /\/(messages?|documents?|evidence|export|search|admin|ai|media|upload|verification|retention|offers|matching|audit|timeline|tasks?|deadlines?|procedures?|parties|register|risks?)\b/i.test(
      pathname,
    )
  ) {
    return false;
  }

  if (pathname === '/api/rechtshilfe/v1/cases') {
    return cacheQueryKeysAllowed(search, new Set(['page', 'limit', 'status']));
  }
  if (pathname === '/api/rechtshilfe/v1/cases/summary') {
    return cacheQueryKeysAllowed(search, new Set());
  }
  if (pathname === '/api/rechtshilfe/v1/profile/me') {
    return cacheQueryKeysAllowed(search, new Set());
  }
  if (pathname === '/api/rechtshilfe/v1/themes/settings') {
    return cacheQueryKeysAllowed(search, new Set());
  }

  return false;
}

/**
 * Rechtshilfe allowlist: draft case JSON create/update, profile upsert, theme CRUD.
 * Never: submit/withdraw/close/hold, messages, documents, evidence, export, admin,
 * verification, AI, media, retention, offers.
 */
export function isRechtshilfeQueueableMutation(method: string, path: string): boolean {
  if (!isMutatingMethod(method)) return false;
  const p = (path.split('?')[0] || '').replace(/\/$/, '');
  const m = (method || '').toUpperCase();

  if (
    /\/(submit|withdraw|close|reopen|hold|resume|timeline|messages?|documents?|evidence|export|search|admin|ai|media|upload|verification|retention|offers|matching|audit|tasks?|deadlines?|procedures?|parties|register|risks?)\b/i.test(
      p,
    )
  ) {
    return false;
  }
  if (/^\/api\/auth\//i.test(p)) return false;

  // Draft case create + field update (no nested actions)
  if (m === 'POST' && p === '/api/rechtshilfe/v1/cases') return true;
  if (m === 'PATCH' && /^\/api\/rechtshilfe\/v1\/cases\/[^/]+$/.test(p)) return true;

  // Own profile upsert
  if (m === 'PUT' && p === '/api/rechtshilfe/v1/profile/me') return true;

  // Theme settings + user theme JSON CRUD
  if (m === 'PUT' && p === '/api/rechtshilfe/v1/themes/settings') return true;
  if (m === 'POST' && p === '/api/rechtshilfe/v1/themes') return true;
  if ((m === 'PUT' || m === 'DELETE') && /^\/api\/rechtshilfe\/v1\/themes\/[^/]+$/.test(p)) {
    return true;
  }

  return false;
}

export function rechtshilfeEntityKeyFromPath(path: string): string | undefined {
  const p = (path.split('?')[0] || '').replace(/\/$/, '');
  if (p === '/api/rechtshilfe/v1/cases') return 'rechtshilfe:cases';
  const caseMatch = p.match(/^\/api\/rechtshilfe\/v1\/cases\/([^/]+)$/);
  if (caseMatch?.[1]) return `rechtshilfe:case:${caseMatch[1]}`;
  if (p === '/api/rechtshilfe/v1/profile/me') return 'rechtshilfe:profile';
  if (p === '/api/rechtshilfe/v1/themes/settings') return 'rechtshilfe:theme-settings';
  if (p === '/api/rechtshilfe/v1/themes') return 'rechtshilfe:themes';
  const theme = p.match(/^\/api\/rechtshilfe\/v1\/themes\/([^/]+)$/);
  return theme?.[1] ? `rechtshilfe:theme:${theme[1]}` : undefined;
}

/**
 * Charity last-good GET: own draft/actions list only.
 * Never: public discovery, staff, contributions, payment, media.
 */
export function isCharityCacheableGet(path: string): boolean {
  const { pathname, search } = splitCachePath(path);

  if (
    /\/(public|staff|contributions?|support|follow|report|media|upload|ai|admin|refund|verifications?|audit)\b/i.test(
      pathname,
    )
  ) {
    return false;
  }

  if (pathname === '/api/charity/v1/actions/mine') {
    return cacheQueryKeysAllowed(search, new Set());
  }

  return false;
}

/**
 * Charity allowlist: draft action JSON CRUD + content-block JSON edits.
 * Never: submit/transition, contributions/payment, staff moderation, public
 * support/follow/report, media upload, AI, refunds.
 */
export function isCharityQueueableMutation(method: string, path: string): boolean {
  if (!isMutatingMethod(method)) return false;
  const p = (path.split('?')[0] || '').replace(/\/$/, '');
  const m = (method || '').toUpperCase();

  if (
    /\/(submit|transition|contributions?|support|follow|report|staff|public|media|upload|ai|admin|refund|verifications?|audit)\b/i.test(
      p,
    )
  ) {
    return false;
  }
  if (/^\/api\/auth\//i.test(p)) return false;

  // Draft action create / update / delete
  if (m === 'POST' && p === '/api/charity/v1/actions') return true;
  if ((m === 'PATCH' || m === 'DELETE') && /^\/api\/charity\/v1\/actions\/[^/]+$/.test(p)) {
    return true;
  }

  // Content blocks on own action (JSON only — no media upload)
  if (m === 'POST' && /^\/api\/charity\/v1\/actions\/[^/]+\/content$/.test(p)) return true;
  if (m === 'POST' && /^\/api\/charity\/v1\/actions\/[^/]+\/content\/reorder$/.test(p)) return true;
  if (
    (m === 'PATCH' || m === 'DELETE')
    && /^\/api\/charity\/v1\/actions\/[^/]+\/content\/[^/]+$/.test(p)
  ) {
    return true;
  }

  return false;
}

export function charityEntityKeyFromPath(path: string): string | undefined {
  const p = (path.split('?')[0] || '').replace(/\/$/, '');
  if (p === '/api/charity/v1/actions' || p === '/api/charity/v1/actions/mine') {
    return 'charity:actions';
  }
  const action = p.match(/^\/api\/charity\/v1\/actions\/([^/]+)(?:\/content(?:\/[^/]+)?)?$/);
  return action?.[1] ? `charity:action:${action[1]}` : undefined;
}

export function computeBackoffMs(attempts: number): number {
  const base = Math.min(60_000, 1000 * 2 ** Math.min(attempts, 6));
  const jitter = Math.floor(Math.random() * 400);
  return base + jitter;
}

export function newIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `idem_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`;
}
